/**
 * JIS配列キーボード + 指ガイド(React SVG。docs/02_game_design.md §3 / docs/03 §4)。
 *
 * canvas ではなく SVG。指ごとの色分けは `logic/keyboard.ts` の定数から引く
 * (ユーザーによる配色カスタムの口)。`nextKeys` のキーは発光強調する。
 *
 * 配置は `logic/keyboardLayout.ts` に切り出してある(手のオーバーレイと座標系を共有するため)。
 */

import { useMemo } from 'react';
import { FINGER_COLORS, highlightedKeyIds, keyLabel } from '../../logic/keyboard';
import { KEYBOARD_LAYOUT, KEY_HEIGHT, LAYOUT_VIEW_BOX } from '../../logic/keyboardLayout';

interface Props {
  readonly nextKeys: readonly string[];
}

export function KeyboardGuide({ nextKeys }: Props) {
  const highlighted = useMemo(() => highlightedKeyIds(nextKeys), [nextKeys]);

  return (
    <svg
      className="keyboard-guide"
      viewBox={LAYOUT_VIEW_BOX}
      role="img"
      aria-label="キーボードガイド"
    >
      {KEYBOARD_LAYOUT.keys.map(({ key, x, y, width }) => {
        const color = FINGER_COLORS[key.finger];
        const active = highlighted.has(key.id);
        const label = keyLabel(key);
        const lines = label.split('\n');
        return (
          <g key={key.id} className={`kb-key ${active ? 'active' : ''}`}>
            <rect
              x={x}
              y={y}
              width={width}
              height={KEY_HEIGHT}
              rx={10}
              fill={color}
              fillOpacity={active ? 1 : 0.22}
              stroke={color}
              strokeWidth={active ? 4 : 2}
            />
            {lines.map((line, index) => (
              <text
                key={index}
                x={x + width / 2}
                y={y + (lines.length === 1 ? KEY_HEIGHT / 2 + 9 : 24 + index * 24)}
                textAnchor="middle"
                className={active ? 'kb-label on' : 'kb-label'}
              >
                {line === ' ' ? '' : line}
              </text>
            ))}
            {key.home ? (
              <rect
                x={x + width / 2 - 10}
                y={y + KEY_HEIGHT - 12}
                width={20}
                height={4}
                rx={2}
                fill="#ffffff"
                fillOpacity={0.8}
              />
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
